// src/components/layout/Header.tsx — SCE Issues
// Top App Bar avec gestion des 3 niveaux (jaune / jaune ou blanc / blanc)

import type { CSSProperties, ReactNode } from 'react'
import { ChevronLeft } from 'lucide-react'
import { LogoSartorius, SignetSartorius } from '@/components/ui/Marque'
import { BUREAU, useMediaQuery } from '@/hooks/useMediaQuery'
// Une seule table des largeurs : l'en-tête doit s'aligner sur la page.
import { LARGEURS_PAGE, type LargeurPage } from './AppShell'

export type HeaderLevel = 1 | 2 | 3

export interface HeaderProps {
  level: HeaderLevel
  title: string
  showBack?: boolean
  onBack?: () => void
  /** Force le fond blanc pour un Level 2 (optionnel) */
  whiteOnLevel2?: boolean
  /** Actions additionnelles à droite (ex: icône × pour formulaire) */
  rightAction?: ReactNode
  /**
   * Bloc centré sur la barre (ex. recherche de l'accueil). Centré sur la
   * BARRE, pas sur la place laissée entre titre et actions : les deux côtés
   * prennent la même largeur quel que soit leur contenu.
   */
  centerAction?: ReactNode
  /**
   * Doit refléter le `width` de l'AppShell qui l'accueille : le bandeau reste
   * pleine largeur (fond jaune de bord à bord), mais son CONTENU s'aligne sur
   * la même colonne que la page. Sans ça, le titre flotte à l'extrême gauche
   * pendant que le contenu est centré.
   */
  width?: LargeurPage
  /**
   * Logo Sartorius devant le titre (accueil seulement, maquette du 29/09) :
   * logo complet au bureau, séparé du nom par un filet ; signet au téléphone.
   */
  marque?: boolean
}

const LARGEURS = LARGEURS_PAGE

/**
 * À partir de cette largeur, le bloc central (recherche) est affiché
 * (RechercheEntete lit la même constante). En deçà, il ne rend rien.
 */
export const ENTETE_LARGE = '(min-width: 1100px)'

/**
 * Top App Bar Sartorius.
 *  - Level 1 → toujours jaune #FFED00
 *  - Level 2 → jaune par défaut, blanc si `whiteOnLevel2`
 *  - Level 3 → toujours blanc
 *
 * Hauteur : 80px, padding horizontal 15px.
 */
export function Header({
  level,
  title,
  showBack,
  onBack,
  whiteOnLevel2,
  rightAction,
  centerAction,
  width = 'content',
  marque = false,
}: HeaderProps): JSX.Element {
  const bureau = useMediaQuery(BUREAU)
  const centreVisible = useMediaQuery(ENTETE_LARGE)
  const isYellow = level === 1 || (level === 2 && !whiteOnLevel2)

  const style: CSSProperties = {
    height: 'var(--spacing-header-h)',
    backgroundColor: isYellow ? 'var(--color-yellow)' : 'var(--color-white)',
    color: 'var(--color-black)',
    borderBottom: isYellow ? 'none' : '1px solid var(--color-gray-20)',
  }

  const retour = showBack && (
    <button
      type="button"
      onClick={onBack}
      aria-label="Retour"
      className="flex items-center justify-center -ml-2"
      style={{ width: 40, height: 40, flexShrink: 0 }}
    >
      <ChevronLeft size={28} strokeWidth={2} />
    </button>
  )

  // Filet entre la marque et le nom de l'outil, au bureau comme au
  // téléphone (demande du 29/09) : un peu plus serré au téléphone.
  const logo = marque && (
    <>
      {bureau ? <LogoSartorius hauteur={17} /> : <SignetSartorius hauteur={24} />}
      <span
        aria-hidden="true"
        style={{ width: 1, height: 24, flexShrink: 0, margin: bureau ? '0 6px' : '0 2px', backgroundColor: 'var(--color-black)', opacity: 0.4 }}
      />
    </>
  )

  return (
    <header className="sticky top-0 z-40" style={style}>
    <div
      className="h-full flex items-center gap-2 w-full"
      style={{
        maxWidth: LARGEURS[width],
        marginInline: 'auto',
        paddingLeft: 15,
        paddingRight: 15,
        // Trois colonnes quand un bloc central est donné : 1fr | auto | 1fr,
        // pour qu'il soit centré sur la barre. Sans recherche affichée (sous
        // 1100 px), le titre prend toute la place laissée par les actions :
        // 1fr | auto | auto (sinon « Tous les problèmes » était coupé à la
        // moitié de la barre au téléphone).
        ...(centerAction
          ? {
              display: 'grid',
              gridTemplateColumns: centreVisible ? 'minmax(0, 1fr) auto minmax(0, 1fr)' : 'minmax(0, 1fr) auto auto',
              gap: centreVisible ? 16 : 8,
            }
          : {}),
      }}
    >
      {centerAction ? (
        <>
          <div className="flex items-center gap-2 min-w-0">
            {retour}
            {logo}
            <h1 className="truncate" style={TITRE}>{title}</h1>
          </div>
          {/* Cellule toujours présente : un bloc central qui ne rend rien
              (recherche masquée au téléphone) ferait glisser les actions de
              droite dans la colonne du milieu. */}
          <div className="flex items-center justify-center">{centerAction}</div>
          <div className="flex items-center justify-end">{rightAction}</div>
        </>
      ) : (
        <>
          {retour}
          {logo}

          <h1 className="flex-1 truncate" style={TITRE}>
            {title}
          </h1>

          {rightAction}
        </>
      )}

    </div>
    </header>
  )
}

const TITRE: CSSProperties = {
  fontSize: 'var(--font-size-copy1)',
  lineHeight: 'var(--line-height-copy1)',
  fontWeight: 500,
  margin: 0,
}
